import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of, tap } from "rxjs";
import { AuthService } from "src/app/features/auth/services/auth.service";
import { environment } from "src/environments/environment";
import { ApiResponse, FetchResponse } from "../models/api-response.interface";
import { AuthDialogService } from "./auth-dialog.service";
import { SnackbarComponent } from "../components/snackbar/snackbar.component";

@Injectable({
  providedIn: "root",
})
export class WishlistApiService {
  apiUrl!: string;
  apiV!: string;
  private wishlistSubject = new BehaviorSubject<string[]>([]);
  wishlist$ = this.wishlistSubject.asObservable();
  private wishlistCountSubject = new BehaviorSubject<number>(0);
  wishlistCount$ = this.wishlistCountSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private authDialogService: AuthDialogService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.apiUrl = environment.apiURL;
    this.apiV = environment.version;

    this.authService.isLoggedIn().subscribe((loggedIn) => {
      if (loggedIn) {
        this.getWishlistCount().subscribe();
      } else {
        this.wishlistSubject.next([]);
        this.wishlistCountSubject.next(0);
      }
    });
  }

  getWishlistCount(): Observable<ApiResponse<number>> {
    return this.httpClient
      .get<ApiResponse<number>>(this.apiUrl + `/api/${this.apiV}/Wishlist/Count`)
      .pipe(
        tap((res) => {
          if (res?.success && typeof res.data === "number") {
            this.wishlistCountSubject.next(res.data);
          }
        })
      );
  }

  getWishlist(
    pageSize: number,
    pageIndex: number
  ): Observable<ApiResponse<FetchResponse<any>>> {
    let params = new HttpParams();
    params = params.append("PageIndex", pageIndex);
    params = params.append("PageSize", pageSize);
    return this.httpClient
      .get<ApiResponse<FetchResponse<any>>>(
        this.apiUrl + `/api/${this.apiV}/Wishlist`,
        { params: params }
      )
      .pipe(
        tap((res) => {
          if (res.success && res.data) {
            this.wishlistSubject.next(
              res.data.items.map((item: any) => item.product.id)
            );
            this.wishlistCountSubject.next(res.data.totalCount || 0); 
          }
        })
      );
  }

  incrementWishlistCount(): void {
    const current = this.wishlistCountSubject.value;
    this.wishlistCountSubject.next(current + 1);
  }

  decrementWishlistCount(): void {
    const current = this.wishlistCountSubject.value;
    this.wishlistCountSubject.next(current > 0 ? current - 1 : 0);
  }

  addToWishlist(productId: string): Observable<ApiResponse<any>> {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.apiUrl}/api/${this.apiV}/Wishlist/${productId}`,
      {},
      { headers: { skipLoading: "true" } }
    );
  }

  removeFromWishlist(productId: string): Observable<ApiResponse<any>> {
    return this.httpClient.delete<ApiResponse<any>>(
      `${this.apiUrl}/api/${this.apiV}/Wishlist/${productId}`,
      { headers: { skipLoading: "true" } }
    );
  }

  /**
   * 🟢 Xử lý Toggle Wishlist tập trung (Centralized):
   * - Kiểm tra đăng nhập (tự bật Auth Modal nếu chưa đăng nhập).
   * - Optimistic Update UI và Header count tức thì.
   * - Gọi API Backend và tự động Rollback nếu lỗi.
   * - Thông báo toast snackbar thành công / thất bại.
   */
  toggleWishlist(productId: string): Observable<boolean> {
    if (!this.authService.hasValidToken()) {
      return new Observable<boolean>((observer) => {
        this.authDialogService.openAuthDialog('login').subscribe((loggedIn) => {
          if (loggedIn) {
            // Cho phép Auth state hoàn tất lưu trước khi gọi lại
            setTimeout(() => {
              this.toggleWishlist(productId).subscribe((res) => {
                observer.next(res);
                observer.complete();
              });
            }, 50);
          } else {
            observer.next(false);
            observer.complete();
          }
        });
      });
    }

    const isFav = this.isInWishlist(productId);
    const previousList = [...this.wishlistSubject.value];

    // 1. Optimistic Update
    if (isFav) {
      this.wishlistSubject.next(previousList.filter((id) => id !== productId));
      this.decrementWishlistCount();
    } else {
      this.wishlistSubject.next(Array.from(new Set([...previousList, productId])));
      this.incrementWishlistCount();
    }

    // 2. Call API
    const action$ = isFav
      ? this.removeFromWishlist(productId)
      : this.addToWishlist(productId);

    return new Observable<boolean>((observer) => {
      action$.subscribe({
        next: (res) => {
          if (!res.success) {
            this.rollback(isFav, previousList, productId);
            this.showSnackbar(this.translate.instant('common.ERROR_RETRY') || 'Đã có lỗi xảy ra', 'error');
            observer.next(isFav);
          } else {
            const msgKey = isFav
              ? 'PRODUCT.MESSAGES.WISHLIST_REMOVED'
              : 'PRODUCT.MESSAGES.WISHLIST_ADDED';
            const defaultMsg = isFav ? 'Đã xóa khỏi yêu thích' : 'Đã thêm vào yêu thích';
            this.showSnackbar(this.translate.instant(msgKey) || defaultMsg, 'success');
            observer.next(!isFav);
          }
          observer.complete();
        },
        error: (err) => {
          // Nếu lỗi do đã tồn tại trong database (400/409 existed), giữ trạng thái active
          if (!isFav && (err?.status === 400 || err?.status === 409)) {
            this.wishlistSubject.next(Array.from(new Set([...this.wishlistSubject.value, productId])));
            observer.next(true);
            observer.complete();
            return;
          }
          this.rollback(isFav, previousList, productId);
          this.showSnackbar(this.translate.instant('common.ERROR_CONNECTION') || 'Lỗi kết nối máy chủ', 'error');
          observer.next(isFav);
          observer.complete();
        },
      });
    });
  }

  private rollback(isFav: boolean, previousList: string[], productId: string): void {
    if (isFav) {
      this.wishlistSubject.next(Array.from(new Set([...previousList, productId])));
      this.incrementWishlistCount();
    } else {
      this.wishlistSubject.next(previousList.filter((id) => id !== productId));
      this.decrementWishlistCount();
    }
  }

  private showSnackbar(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(SnackbarComponent, {
      data: { message, type },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['custom-snackbar'],
    });
  }

  /** Lấy danh sách ID hiện tại */
  getCurrentWishlist(): string[] {
    return this.wishlistSubject.value;
  }

  /** Cập nhật state trong service (component tự gọi khi cần) */
  updateWishlistState(productIds: string[]): void {
    this.wishlistSubject.next(productIds);
  }

  /** Kiểm tra product có trong wishlist không */
  isInWishlist(productId: string): boolean {
    return this.wishlistSubject.value.includes(productId);
  }
}
